import React, { useEffect, useRef, useState } from 'react';
import { authService, API_HOST } from '../services/api';
import { getRoleDisplayName } from '../utils/roleDisplayNames';
import { validateNewPassword } from '../utils/passwordPolicy';
import EmptyState from './EmptyState';

/**
 * ProfileModal — Phase 9 item 2f
 *
 * Opens from the Header avatar dropdown. Shows:
 *  - Profile photo (upload)
 *  - Read-only: Name, Username, Email
 *  - Editable: Phone / Contact
 *  - Password change form
 *  - Read-only: Role + list of accessible entities
 */

interface ProfileModalProps {
  onClose: () => void;
}

interface ProfileData {
  user_id: number;
  username: string;
  email: string;
  full_name?: string;
  role: string;
  phone?: string | null;
  avatar_url?: string | null;
  organization_id?: number | null;
  organization_name?: string | null;
  tenant_name?: string | null;
  department_name?: string | null;
  accessible_entities?: Array<{ organization_id: number; name: string }>;
}

// Shared design tokens for the modal (kept at module scope so the 2FA section
// below reuses the exact same font + gradient-green primary button).
const FONT_STACK = "'Poppins', sans-serif";
const PRIMARY_GRADIENT = 'linear-gradient(135deg, #1E3A8A 0%, #16A34A 100%)';
const primaryBtn: React.CSSProperties = {
  background: PRIMARY_GRADIENT, color: '#FFFFFF', border: 'none', borderRadius: '20px',
  padding: '8px 14px', fontSize: '13px', fontWeight: 500, fontFamily: FONT_STACK, cursor: 'pointer',
};

const ProfileModal: React.FC<ProfileModalProps> = ({ onClose }) => {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [phone, setPhone] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPw, setChangingPw] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await authService.getMyProfile();
      setProfile(res.data);
      setPhone((res.data as any).phone || '');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSavePhone = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await authService.updateMyProfile({ phone });
      setSuccess('Contact number updated');
      setTimeout(() => setSuccess(null), 2500);
      await loadProfile();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setError(null); setSuccess(null);
    if (!currentPassword || !newPassword) {
      setError('Enter current and new password');
      return;
    }
    const policyError = validateNewPassword(newPassword);
    if (policyError) {
      setError(policyError);
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }
    setChangingPw(true);
    try {
      await authService.changeMyPassword({ current_password: currentPassword, new_password: newPassword });
      setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
      setSuccess('Password changed successfully');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPw(false);
    }
  };

  // Keep the header avatar (and any other view reading the cached user) in sync:
  // update localStorage and broadcast so the top-right avatar reflects the change
  // immediately, without a full reload.
  const syncAvatarEverywhere = (url: string | null) => {
    try {
      const raw = localStorage.getItem('user');
      if (raw) { const u = JSON.parse(raw); u.avatar_url = url; localStorage.setItem('user', JSON.stringify(u)); }
    } catch { /* non-fatal */ }
    window.dispatchEvent(new CustomEvent('pb:avatar-updated', { detail: { avatar_url: url } }));
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadingAvatar(true); setError(null);
    try {
      const res = await authService.uploadMyAvatar(f);
      await loadProfile();
      syncAvatarEverywhere((res.data as any)?.avatar_url ?? null);
      setSuccess('Profile photo updated');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    setUploadingAvatar(true); setError(null);
    try {
      await authService.removeMyAvatar();
      await loadProfile();
      syncAvatarEverywhere(null);
      setSuccess('Profile photo removed');
      setTimeout(() => setSuccess(null), 2500);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to remove photo');
    } finally {
      setUploadingAvatar(false);
    }
  };

  const fontStack = FONT_STACK;
  const labelStyle: React.CSSProperties = { fontSize: '11px', fontWeight: 600, color: 'var(--color-gray-500)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '4px' };
  const valueStyle: React.CSSProperties = { fontSize: '13px', color: 'var(--color-gray-900)', marginBottom: '12px' };
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '20px',
    fontSize: '13px',
    fontFamily: fontStack,
    background: 'var(--card-background)',
  };
  const btnPrimary = primaryBtn;
  // Secondary / pill actions (Upload / Remove photo) — match the app's rounded style.
  const btnSecondary: React.CSSProperties = {
    fontSize: '12px', padding: '6px 12px', borderRadius: '20px',
    border: '1px solid var(--color-gray-300)', background: 'var(--card-background)',
    color: 'var(--color-gray-700)', fontFamily: fontStack, cursor: 'pointer',
  };
  const btnDanger: React.CSSProperties = { ...btnSecondary, color: '#B91C1C', borderColor: '#FCA5A5' };

  const displayName = profile?.full_name || profile?.username || 'User';
  const initials = displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  const avatarSrc = profile?.avatar_url ? `${API_HOST}${profile.avatar_url}` : null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        paddingTop: '64px',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: '640px',
          background: 'var(--card-background)',
          borderRadius: '20px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          fontFamily: fontStack,
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 100px)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>My Profile</h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: 'var(--color-gray-500)' }} aria-label="Close">
            &times;
          </button>
        </div>

        <div style={{ padding: '20px', overflowY: 'auto' }}>
          {loading ? (
            <EmptyState flat text="Loading profile…" />
          ) : !profile ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#DC2626' }}>{error || 'Unable to load profile'}</div>
          ) : (
            <>
              {/* Avatar + basic info */}
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', marginBottom: '20px' }}>
                <div
                  style={{
                    width: '72px', height: '72px', borderRadius: '50%',
                    background: avatarSrc ? `url(${avatarSrc}) center/cover` : 'linear-gradient(135deg, #1E3A8A 0%, #16A34A 100%)',
                    color: '#FFFFFF',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '22px', fontWeight: 600,
                    flexShrink: 0,
                    border: '2px solid #FFFFFF',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.12)',
                  }}
                >
                  {!avatarSrc && initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '16px', fontWeight: 600, color: 'var(--color-gray-900)' }}>{displayName}</div>
                  <div style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '2px' }}>
                    {getRoleDisplayName(profile.role)}
                    {profile.organization_name && <> · {profile.organization_name}</>}
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAvatar}
                      style={btnSecondary}
                    >
                      {uploadingAvatar ? 'Working…' : (avatarSrc ? 'Change photo' : 'Upload photo')}
                    </button>
                    {avatarSrc && (
                      <button onClick={handleRemoveAvatar} disabled={uploadingAvatar} style={btnDanger}>
                        Remove photo
                      </button>
                    )}
                  </div>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleAvatarChange} style={{ display: 'none' }} />
                </div>
              </div>

              {/* Messages */}
              {error && <div style={{ padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: '10px', marginBottom: '10px', fontSize: '12px' }}>{error}</div>}
              {success && <div style={{ padding: '8px 12px', background: '#D1FAE5', color: '#065F46', borderRadius: '10px', marginBottom: '10px', fontSize: '12px' }}>{success}</div>}

              {/* Read-only identity block */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '20px' }}>
                <div>
                  <div style={labelStyle}>Full Name</div>
                  <div style={valueStyle}>{profile.full_name || '—'}</div>
                </div>
                <div>
                  <div style={labelStyle}>Username</div>
                  <div style={valueStyle}>{profile.username}</div>
                </div>
                <div>
                  <div style={labelStyle}>Email</div>
                  <div style={valueStyle}>{profile.email}</div>
                </div>
                <div>
                  <div style={labelStyle}>Role</div>
                  <div style={valueStyle}>{getRoleDisplayName(profile.role)}</div>
                </div>
              </div>

              {/* Editable: Phone */}
              <div style={{ padding: '14px', border: '1px solid var(--color-gray-200)', borderRadius: '20px', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Contact Number</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    style={{ ...inputStyle, flex: 1 }}
                  />
                  <button onClick={handleSavePhone} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </div>

              {/* Change password */}
              <div style={{ padding: '14px', border: '1px solid var(--color-gray-200)', borderRadius: '20px', marginBottom: '16px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>Change Password</div>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <input type="password" placeholder="Current password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} style={inputStyle} />
                  <input type="password" placeholder="New password (min 8 chars)" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={inputStyle} />
                  <input type="password" placeholder="Confirm new password" value={confirmNewPassword} onChange={e => setConfirmNewPassword(e.target.value)} style={inputStyle} />
                  <button onClick={handleChangePassword} disabled={changingPw} style={{ ...btnPrimary, opacity: changingPw ? 0.7 : 1, marginTop: '4px', alignSelf: 'flex-start' }}>
                    {changingPw ? 'Changing…' : 'Change Password'}
                  </button>
                </div>
              </div>

              {/* Two-factor auth (audit 1.4 #12) */}
              <TwoFactorSection />

              {/* Accessible entities */}
              <div style={{ padding: '14px', border: '1px solid var(--color-gray-200)', borderRadius: '20px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
                  Entities with Access ({profile.accessible_entities?.length || 0})
                </div>
                {profile.accessible_entities && profile.accessible_entities.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '180px', overflowY: 'auto' }}>
                    {profile.accessible_entities.map(e => (
                      <div key={e.organization_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--color-gray-50)', borderRadius: '10px', fontSize: '12px' }}>
                        <span style={{ color: 'var(--color-gray-900)' }}>{e.name}</span>
                        <span style={{ color: 'var(--color-gray-500)', textTransform: 'capitalize' }}>Organization</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: '12px', color: 'var(--color-gray-400)' }}>No accessible entities.</div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const TwoFactorSection: React.FC = () => {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [setup, setSetup] = useState<{ secret: string; otpauth_uri: string } | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { authService.twoFAStatus().then(r => setEnabled(r.data.enabled)).catch(() => setEnabled(false)); }, []);

  const begin = async () => {
    setMsg(''); setBusy(true);
    try { setSetup((await authService.twoFASetup()).data); } catch (e: any) { setMsg(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const enable = async () => {
    setMsg(''); setBusy(true);
    try { await authService.twoFAEnable(code); setEnabled(true); setSetup(null); setCode(''); setMsg('Two-factor is now on.'); }
    catch (e: any) { setMsg(e?.response?.data?.detail || 'Invalid code'); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    const c = window.prompt('Enter a current authenticator code to turn off 2FA:');
    if (!c) return;
    setBusy(true);
    try { await authService.twoFADisable(c); setEnabled(false); setMsg('Two-factor turned off.'); }
    catch (e: any) { setMsg(e?.response?.data?.detail || 'Invalid code'); }
    finally { setBusy(false); }
  };

  const box: React.CSSProperties = { padding: '14px', border: '1px solid var(--color-gray-200)', borderRadius: '20px', marginBottom: '16px', fontFamily: FONT_STACK };
  const inp: React.CSSProperties = { padding: '8px 12px', border: '1px solid var(--color-gray-300)', borderRadius: 20, fontSize: 13, fontFamily: FONT_STACK, background: 'var(--card-background)' };

  return (
    <div style={box}>
      <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '8px' }}>
        Two-factor authentication {enabled === true && <span style={{ color: '#16A34A', fontSize: 11 }}>· ON</span>}
      </div>
      {enabled ? (
        <button onClick={disable} disabled={busy} style={{ fontSize: 12, color: '#B91C1C', border: '1px solid #FECACA', background: '#FEF2F2', borderRadius: 20, padding: '6px 14px', cursor: 'pointer', fontFamily: FONT_STACK }}>
          Turn off 2FA
        </button>
      ) : setup ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ fontSize: 12, color: 'var(--color-gray-500)' }}>Scan this secret in your authenticator app, then enter a code to confirm.</div>
          <code style={{ fontSize: 12, background: 'var(--color-gray-100)', padding: '8px 12px', borderRadius: 10, wordBreak: 'break-all' }}>{setup.secret}</code>
          <input value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" style={{ ...inp, letterSpacing: '0.3em', textAlign: 'center' }} />
          <button onClick={enable} disabled={busy || code.length < 6} style={{ ...primaryBtn, alignSelf: 'flex-start', opacity: (busy || code.length < 6) ? 0.7 : 1 }}>
            {busy ? 'Enabling…' : 'Enable 2FA'}
          </button>
        </div>
      ) : (
        <button onClick={begin} disabled={busy || enabled === null} style={{ ...primaryBtn, opacity: (busy || enabled === null) ? 0.7 : 1 }}>
          Set up 2FA
        </button>
      )}
      {msg && <div style={{ fontSize: 12, color: 'var(--color-gray-500)', marginTop: 8 }}>{msg}</div>}
    </div>
  );
};

export default ProfileModal;
